import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { transparenceData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import { Shield, Database, FileText, Mail, ArrowRight, Download, Lock, Eye } from 'lucide-react';

const TransparencePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = transparenceData[language] || transparenceData.fr;

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <div className="flex justify-center gap-4 mb-6">
                <Lock className={`w-8 h-8 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
                <Eye className={`w-8 h-8 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              </div>
              <h1 className="heading-1 mb-4">{content.title}</h1>
              <div className="divider mx-auto mb-6" />
              <p className="body-large">{content.subtitle}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Personal Data Section */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                <div className={`p-6 ${isDark ? 'bg-[#5B2D8E]/10' : 'bg-[#5B2D8E]/5'} border-b ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-[#5B2D8E]/30' : 'bg-[#5B2D8E]/10'} flex items-center justify-center`}>
                      <Database className={`w-6 h-6 ${isDark ? 'text-[#9B7FCE]' : 'text-[#5B2D8E]'}`} />
                    </div>
                    <h2 className="heading-2">{content.sections[0]?.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {content.sections[0]?.content.map((item, index) => (
                    <div key={index} className={`pb-6 ${index < content.sections[0].content.length - 1 ? `border-b ${isDark ? 'border-white/10' : 'border-stone-100'}` : ''}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                        {item.label}
                      </h3>
                      <p className="text-theme-secondary">{item.text}</p>
                    </div>
                  ))}
                  
                  {/* Contact for privacy */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-[#292524]' : 'bg-[#F5F0E8]'} flex items-center gap-4`}>
                    <Mail className={`w-6 h-6 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
                    <div>
                      <span className="text-sm text-theme-secondary">Contact vie privée :</span>
                      <a href="mailto:privacy@ada.org" className={`block font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                        privacy@ada.org
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Financial Transparency Section */}
      <section className={`section ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#0C0A09]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                <div className={`p-6 ${isDark ? 'bg-green-500/10' : 'bg-green-50'} border-b ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-green-500/30' : 'bg-green-100'} flex items-center justify-center`}>
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="heading-2">{content.sections[1]?.title}</h2>
                  </div>
                </div>
                <div className="p-6">
                  {/* Info items */}
                  <div className="space-y-4 mb-8">
                    {content.sections[1]?.content.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <span className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-theme-primary">{item.label} : </span>
                          <span className="text-theme-secondary">{item.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Reports Table */}
                  <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                    <div className={`px-4 py-3 ${isDark ? 'bg-white/5' : 'bg-stone-50'} border-b ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                      <h3 className="font-semibold text-theme-primary">Rapports disponibles</h3>
                    </div>
                    <div className="divide-y divide-stone-200 dark:divide-white/10">
                      {content.sections[1]?.reports.map((report, index) => (
                        <div key={index} className="px-4 py-3 flex items-center justify-between">
                          <div>
                            <span className="font-medium text-theme-primary">{report.period}</span>
                          </div>
                          <div>
                            {report.url ? (
                              <a 
                                href={report.url}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}
                              >
                                <Download className="w-4 h-4" />
                                Télécharger
                              </a>
                            ) : (
                              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm ${isDark ? 'bg-white/10 text-stone-400' : 'bg-stone-100 text-stone-500'}`}>
                                {report.status}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <Shield className="w-16 h-16 mx-auto mb-6 text-white/80" />
              <h2 className="heading-2 text-white mb-4">La transparence, c'est la confiance</h2>
              <p className="text-xl text-white/80 mb-8">
                Nous croyons qu'un mouvement politique doit rendre des comptes. C'est pourquoi nous publions nos positions, nos comptes et nos résultats.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gouvernance" className="btn-secondary">
                  Notre gouvernance
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/scorecard" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors">
                  Voir le Scorecard
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TransparencePage;
